import { defineStore } from 'pinia';
import { useServiceStore } from '@modules/service/m-service';
import SessionCache from '@plugins/cache/index';
import { DOCUMENT_USER_STATE } from '../service/constants';
const cache = new SessionCache('documents');

export const useDocumentsStore = defineStore('m-documents', {
	state: () => ({
		assistedDocumentsFilters: {
			origin: null,
			documentType: null,
			documentSubtype: null,
			state: null,
		},
		assistedDocumentsAll: {
			pages: [],
			pageNumber: 1,
			hasMorePages: null,
			paginationKey: null,
		},
		assistedDocumentsUser: {
			pages: [],
			pageNumber: 1,
			hasMorePages: null,
			paginationKey: null,
			products: [], //For document form combo
		},
	}),
	actions: {
		/*istanbul ignore next */
		setAssistedDocumentsFilters(filters) {
			this.assistedDocumentsFilters = filters;
		},

		setAssistedDocumentsAllPages(pages) {
			this.assistedDocumentsAll.pages = pages;
		},
		setAssistedDocumentsAllPageNumber(pageNumber) {
			this.assistedDocumentsAll.pageNumber = pageNumber;
		},
		setAssistedDocumentsAllHasMorePages(hasMorePages) {
			this.assistedDocumentsAll.hasMorePages = hasMorePages;
		},
		setAssistedDocumentsAllPaginationKey(paginationKey) {
			this.assistedDocumentsAll.paginationKey = paginationKey;
		},
		setAssistedDocumentsAllPage({ page, number }) {
			this.assistedDocumentsAll.pages[number] = page;
		},
		findAssistedDocumentsAll(documentId) {
			const pageIndex = this.assistedDocumentsAll.pages.findIndex((page) =>
				page.some((product) => product.id === documentId)
			);
			/*istanbul ignore if */
			if (pageIndex === -1) return null;

			const rowIndex = this.assistedDocumentsAll.pages[pageIndex].findIndex(
				(product) => product.id === documentId
			);
			/*istanbul ignore if */
			if (rowIndex === -1) return null;

			return this.assistedDocumentsAll.pages[pageIndex][rowIndex];
		},

		setAssistedDocumentsUserPages(pages) {
			this.assistedDocumentsUser.pages = pages;
		},
		setAssistedDocumentsUserPageNumber(pageNumber) {
			this.assistedDocumentsUser.pageNumber = pageNumber;
		},
		setAssistedDocumentsUserHasMorePages(hasMorePages) {
			this.assistedDocumentsUser.hasMorePages = hasMorePages;
		},
		setAssistedDocumentsUserPaginationKey(paginationKey) {
			this.assistedDocumentsUser.paginationKey = paginationKey;
		},
		setAssistedDocumentsUserPage({ page, number }) {
			this.assistedDocumentsUser.pages[number] = page;
		},
		/*istanbul ignore next */
		setAssistedDocumentsUserProducts(products) {
			this.assistedDocumentsUser.products = products;
		},
		findAssistedDocumentsUser(documentId) {
			const pageIndex = this.assistedDocumentsUser.pages.findIndex((page) =>
				page.some((product) => product.id === documentId)
			);
			/*istanbul ignore if */
			if (pageIndex === -1) return null;

			const rowIndex = this.assistedDocumentsUser.pages[pageIndex].findIndex(
				(product) => product.id === documentId
			);
			/*istanbul ignore if */
			if (rowIndex === -1) return null;

			return this.assistedDocumentsUser.pages[pageIndex][rowIndex];
		},

		getSignatureState({ processId }) {
			const url = `/documents/${processId}/signature`;
			const method = 'GET';

			const serviceStore = useServiceStore();

			return serviceStore.request({
				service: {
					request: {
						url,
						method,
					},
				},
			});
		},

		getAssistedDocuments({
			companyId,
			userId = undefined,
			origin = undefined,
			state = undefined,
			productId = undefined,
			paginationKey = undefined,
		}) {
			const url = `/documents/assisted-channels/userDocuments`;
			const method = 'GET';
			const queryParams = { companyId, userId, origin, state, productId, paginationKey };
			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data.body);
		},

		getAssistedDocument(documentId) {
			const url = `/documents/userDocuments/${documentId}`;
			const method = 'GET';
			const serviceStore = useServiceStore();
			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data.body);
		},

		getInvestmentDocuments({ productId, paginationKey = undefined }) {
			const url = `/documents`;
			const method = 'GET';
			const queryParams = { productId, paginationKey, documentTypeId: '9702' };
			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data);
		},

		getInvestmentDocument({ documentId }) {
			const url = `/documents/${documentId}`;
			const method = 'GET';
			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data);
		},

		getUserDocuments({ refresh, paginationKey, documentOrigin, companyId }) {
			const serviceStore = useServiceStore();
			const url = `/documents/userDocuments`;
			const method = 'GET';
			const queryParams = { companyId, paginationKey, origin: documentOrigin };

			const cacheKey = `documents/userDocuments/${documentOrigin}`;

			/*istanbul ignore if */
			if (refresh) {
				SessionCache.clear(cacheKey);
			}

			/*istanbul ignore if */
			if (cache.has(cacheKey) && !paginationKey) {
				const data = cache.get(cacheKey);
				return Promise.resolve(data);
			}

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => {
					const response = {
						paging: data.paging,
						data: data?.data?.map((item) => {
							// We create cache of each item to later modify cache if necesary
							cache.set(`allDocuments/${item?.id}`, item);
							return item;
						}),
					};
					/*istanbul ignore if */
					if (cache.has(cacheKey)) {
						const result = cache.get(cacheKey);
						Object.assign(response.data, result.data.concat(response.data));
					}
					cache.set(cacheKey, response);
					return response;
				});
		},

		getAssistedUserDocuments({
			data,
			source,
			origin,
			companyId,
			documentOrigin,
			documentType,
			documentSubtype,
			state,
			paginationKey,
		}) {
			const secureStore = useSecureStore();
			const serviceStore = useServiceStore();
			const url = `/documents/userDocuments`;
			const method = 'GET';
			const queryParams = {
				companyId,
				origin: documentOrigin,
				documentType,
				documentSubtype,
				state,
				paginationKey,
			};

			const { userId } = data;
			const userUUID = secureStore.getUuid;
			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => {
					source.postMessage(
						{
							name: 'get-user-documents',
							payload: data,
							userUUID,
							userId,
						},
						origin
					);
				})
				.catch((err) => {
					const payload = { data: err.response.data };
					source.postMessage(
						{
							name: 'get-user-documents',
							payload: payload,
							userUUID,
							userId,
						},
						origin
					);
				});
		},

		patchDocument({ documentId, description, state, subtype }) {
			const url = `/documents/userDocuments/${documentId}`;
			const method = 'PATCH';
			const serviceStore = useServiceStore();
			const payload = {};
			/*istanbul ignore else */

			if (state) payload.state = state;
			if (description) payload.description = description;
			/*istanbul ignore else */
			if (subtype) payload.subtypeDto = subtype;

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then(({ data }) => data.body);
		},

		patchDocumentAsRead(documentId) {
			const readState = DOCUMENT_USER_STATE.READ;
			return this.patchDocument({ documentId, state: readState }).then((body) => {
				const cacheData = cache.get(`allDocuments/${documentId}`);

				if (cacheData) {
					cacheData.state = String(readState);
				}

				return body;
			});
		},

		patchAssistedDocument({ documentId, description, state, subtype, subtypeDescription }) {
			return this.patchDocument({ documentId, description, state, subtype }).then((body) => {
				const loadedDocument = this.findAssistedDocumentsAll(documentId);
				/*istanbul ignore else */
				if (loadedDocument) {
					/*istanbul ignore else */
					if (state) loadedDocument.state = String(state);
					/*istanbul ignore else */
					if (subtype) loadedDocument.subtypeId = String(subtype);
					/*istanbul ignore if */
					if (subtypeDescription) loadedDocument.subtypeDescription = subtypeDescription;
					if (description) loadedDocument.description = description;
				}

				const loadedUserDocument = this.findAssistedDocumentsUser(documentId);
				/*istanbul ignore else */
				if (loadedUserDocument) {
					/*istanbul ignore else */
					if (state) loadedUserDocument.state = String(state);
					/*istanbul ignore else */
					if (subtype) loadedUserDocument.subtypeId = String(subtype);
					/*istanbul ignore if */
					if (subtypeDescription) loadedUserDocument.subtypeDescription = subtypeDescription;
					if (description) loadedUserDocument.description = description;
				}
				return body;
			});
		},

		getAssistedDocumentSubtypes() {
			const url = `/documents/userDocuments/subtypes`;
			const method = 'GET';
			const serviceStore = useServiceStore();
			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(({ data }) => data.body);
		},

		async postAssistedUserDocument({
			data,
			source,
			origin,
			documentType,
			documentSubtypeId,
			productId,
			description,
			format,
			file,
			totalSize,
		}) {
			const secureStore = useSecureStore();

			const url = `/documents/userDocuments`;
			const method = 'POST';
			const { userId } = data;
			const userUUID = secureStore.getUuid;

			let payload = {
				documentType,
				documentSubtypeId,
				productId,
				description,
				format,
				totalSize,
			};

			const lastResponse = await this.sendInChunks({ url, method, payload, file });

			source.postMessage(
				{
					name: 'post-user-document',
					payload: lastResponse.data,
					userUUID,
					userId,
				},
				origin
			);

			return lastResponse.data;
		},

		async postUserDocument(
			{ documentType, documentSubtypeId, productId, description, format, file, totalSize },
			onProgress
		) {
			const url = `/documents/userDocuments`;
			const method = 'POST';

			let payload = {
				documentType,
				documentSubtypeId,
				productId,
				description,
				format,
				totalSize,
			};

			const lastResponse = await this.sendInChunks({ url, method, payload, file, onProgress });
			return lastResponse.data;
		},

		async postAssistedUserDocumentRequest({
			data,
			source,
			origin,
			documentType,
			documentSubtype,
			productId,
			description,
		}) {
			const secureStore = useSecureStore();
			const serviceStore = useServiceStore();
			const url = `/documents/userDocuments/request`;
			const method = 'POST';
			const { userId } = data;
			const userUUID = secureStore.getUuid;

			let payload = {
				documentType,
				documentSubtype,
				productId,
				description,
			};

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload,
				})
				.then((res) => {
					source.postMessage(
						{
							name: 'post-user-document-request',
							payload: res.data,
							userUUID,
							userId,
						},
						origin
					);
				});
		},

		async putUserDocumentRequest({ documentId, format, file, totalSize }, onProgress) {
			const url = `/documents/userDocuments/request`;
			const method = 'PUT';

			let payload = {
				documentId,
				format,
				file,
				totalSize,
			};

			const lastResponse = await this.sendInChunks({ url, method, payload, file, onProgress });

			return lastResponse.data;
		},

		async sendInChunks({ url, method, payload, file, onProgress }) {
			const serviceStore = useServiceStore();
			const secureStore = useSecureStore();
			const userUUID = secureStore.getUuid;
			const chunks = [];
			const chunkSize = 3 * 1000 * 1000;
			const moreThanOne = file.length > chunkSize;
			let previousResponse;
			/*istanbul ignore if */
			if (moreThanOne) payload.fileSessionId = userUUID;

			for (let i = 0; i < file.length; i += chunkSize) {
				chunks.push(file.slice(i, i + chunkSize));
			}

			const totalChunks = chunks.length;

			for (let i = 0; i < totalChunks; i++) {
				payload.lastPart = i === totalChunks - 1;
				payload.file = chunks[i];

				// eslint-disable-next-line no-await-in-loop
				previousResponse = await serviceStore.request({
					service: {
						request: {
							url,
							method,
						},
					},
					payload: { ...payload },
				});
				//Allows to set the percent on a variable inside the onProgress function
				if (onProgress) {
					const percent = Math.round(((i + 1) / totalChunks) * 100);
					onProgress(percent);
				}
			}

			return previousResponse;
		},
	},
});
